import React from "react";
import { Switch, Form, Input, Flex, Button, Select, InputNumber } from "antd";
import TextArea from "antd/es/input/TextArea";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import { buildMasterColumnOptions } from "../../../utils/masterHelper";


const sortOptions = [
  { label: "Ascending", value: "ascending" },
  { label: "Descending", value: "descending" },
];

// Keep in sync with the profile fields TextForm exposes.
const profileFieldOptions = [
  { label: "Name", value: "name" },
  { label: "Email", value: "email" },
  { label: "Employee ID", value: "employeeId" },
];

const frequencyOptions = [
  // {
  //     "label": "Weekly",
  //     "value": "Weekly"
  // },
  {
    "label": "Monthly",
    "value": "Monthly"
  },
  {
    "label": "Quarterly",
    "value": "Quarterly"
  },
  {
    "label": "Semi-annually",
    "value": "Semi-annually"
  },
  {
    "label": "Yearly",
    "value": "Yearly"
  }
]

const DropdownForm = ({ formValues, form, isFrequency = false, showPreviousDataFetch = false, ...rest }) => {
  const { t } = useTranslation();
  let isDisabled = rest?.isOrgStructure || rest.parentTable;
  const { masterList } = useSelector((state) => state.masterData);
  // Only dropdowns backed by a master data table can pull values from a row.
  const isMasterDataBacked = Boolean(rest?.parentTable || rest?.isMasterData);
  // `options` is unregistered while the list is hidden, so fall back to the
  // element's own options for the count.
  const masterOptionCount = (formValues?.options || rest?.options || []).length;

  // Function to trigger component re-render

  const handleFrequencyChange = (checked) => {
    if (checked) {
      // Set both isFrequency and options together to avoid race condition
      form.setFieldsValue({
        isFrequency: true,
        options: frequencyOptions
      })
    } else {
      // Clear options when turning off frequency
      form.setFieldsValue({
        isFrequency: false,
        options: []
      })
    }
  }

  const optionsCount = (formValues?.options || []).length;

  // Default the selection limit to the total number of options whenever the
  // field is visible but unset (users can still override it).
  React.useEffect(() => {
    if (
      formValues?.multiSelect &&
      formValues?.enableScoring &&
      form.getFieldValue("maxSelectableOptions") == null
    ) {
      form.setFieldsValue({ maxSelectableOptions: optionsCount });
    }
  }, [formValues?.multiSelect, formValues?.enableScoring, optionsCount]);

  const handleMasterUpdate = () => {
    const currentMaster = masterList.find(master => master.dataId === rest?.parentTable)
    const columnLabel = rest.mappedColumn || rest.label
    if (currentMaster && columnLabel) {
      form.setFieldsValue({
        options: buildMasterColumnOptions(currentMaster, columnLabel, masterList)
      })
    }
  }


  return (
    <>
      <Form.Item name="label" label={t("Label")}>
        <Input placeholder={t("Enter label")} />
      </Form.Item>
      <Form.Item name="placeholder" label={t("Placeholder")}>
        <Input placeholder={t("Enter placeholder")} />
      </Form.Item>
      <Form.Item
        name="tooltip"
        label={t("Tooltip")}
        className="horizontal-form-item"
      >
        <Switch size="small" />
      </Form.Item>
      {formValues && formValues.tooltip && (
        <Form.Item label="" name="helpText">
          <TextArea rows={2} placeholder={t("Enter help text")} />
        </Form.Item>
      )}
      <Form.Item
        name="mandatory"
        label={t("Mandatory")}
        className="horizontal-form-item"
      >
        <Switch size="small" />
      </Form.Item>
      <Form.Item
        name="readOnly"
        label={t("Read Only")}
        className="horizontal-form-item"
      >
        <Switch size="small" />
      </Form.Item>
      <Form.Item
        name="hideInitially"
        label={t("Hide Initially")}
        className="horizontal-form-item"
      >
        <Switch size="small" />
      </Form.Item>

      <Form.Item
        name="highlight"
        label={t("Highlight")}
        className="horizontal-form-item"
      >
        <Switch size="small" />
      </Form.Item>
      <Form.Item
        name="multiSelect"
        label={t("Multi-Select")}
        className="horizontal-form-item"
      >
        <Switch size="small" />
      </Form.Item>
      <Form.Item
        name="isFrequency"
        label={t("Is Frequency")}
        className="horizontal-form-item"
      >
        <Switch size="small" onChange={handleFrequencyChange} />
      </Form.Item>
      {showPreviousDataFetch && (
        <Form.Item
          name="enablePreviousDataFetch"
          label={t("Enable Previous Data Fetch")}
          className="horizontal-form-item"
        >
          <Switch size="small" />
        </Form.Item>
      )}

      <Form.Item name="enableScoring" label={t("Enable Scoring")} className="horizontal-form-item">
        <Switch size="small" />
      </Form.Item>
      {formValues?.multiSelect && formValues?.enableScoring && (
        <Form.Item
          name="maxSelectableOptions"
          label={t("Max Options to be selected")}
        >
          <InputNumber
            min={1}
            max={(formValues?.options || []).length || undefined}
            style={{ width: "100%" }}
            placeholder={t("Enter max options to be selected")}
          />
        </Form.Item>
      )}
      {isMasterDataBacked && (
        <>
          {/* Takes the field in or out of master data autofill on both sides. Off
              means inert: never filled from a row, and never rewriting the fields
              that are. */}
          <Form.Item
            name="autoPopulateMaster"
            label={t("Autopopulate from master data row")}
            tooltip={t("Keeps this field in sync with the other fields built on the same master data. Turn it off to leave the field out entirely: it is not filled from a row, and changing it does not update the others.")}
            className="horizontal-form-item"
            valuePropName="checked"
          >
            <Switch size="small" />
          </Form.Item>
          {/* Preselects the master data row whose text matches the signed-in
              user's profile value. */}
          <Form.Item
            name="mapProfile"
            label={t("Map Profile")}
            className="horizontal-form-item"
            valuePropName="checked"
          >
            <Switch
              size="small"
              onChange={(checked) => {
                if (!checked) {
                  form.setFieldsValue({ profileField: undefined });
                }
              }}
            />
          </Form.Item>
          {formValues?.mapProfile && (
            <Form.Item name="profileField" label={t("Select Profile Field")}>
              <Select
                placeholder={t("Select profile field")}
                allowClear
                options={profileFieldOptions.map((option) => ({
                  label: t(option.label),
                  value: option.value,
                }))}
              />
            </Form.Item>
          )}
        </>
      )}
      {/* Master data backed dropdowns source their options from the connected
          table, so show just how many rows came across instead of the full
          read-only list. */}
      {isMasterDataBacked ? (
        <div style={{ marginBottom: "12px" }}>
          {t("List options")}{" "}
          <span className="text-color-secondary">({masterOptionCount})</span>
        </div>
      ) : (
        <>
      <div style={{ marginBottom: "12px" }}>{t("List options")}</div>{" "}
      <Form.List name="options">
        {(fields, { add, remove }) => {
          const validateUniqueOption = (_, value) => {
            if (!value || !value.trim()) {
              return Promise.resolve();
            }

            const currentValues = form.getFieldsValue();
            const options = currentValues.options || [];
            const trimmedValue = value.toLowerCase().trim();

            // Count how many times this value appears
            const count = options.filter(
              (opt) =>
                opt &&
                opt.label &&
                opt.label.toLowerCase().trim() === trimmedValue
            ).length;

            if (count > 1) {
              return Promise.reject(new Error("Option already exists"));
            }

            return Promise.resolve();
          };

          return (
            <>
              {fields.map((field) => (
                <Flex gap={12} justify="space-between" key={field.key}>
                  {" "}
                  <Form.Item
                    {...field}
                    name={[field.name, "label"]}
                    style={{ marginBottom: "20px", width: "100%" }}
                    rules={[
                      { required: true, whitespace: true, message: t("Option is required") },
                      { validator: validateUniqueOption },
                    ]}
                  >
                    <Input
                      disabled={isDisabled || formValues?.isFrequency}
                      placeholder={t("Enter option")}
                      suffix={
                        !isDisabled && !formValues?.isFrequency && (
                          <DeleteOutlined
                            style={{ color: "red", cursor: "pointer" }}
                            onClick={() => {
                              remove(field.name);
                              // setTimeout(triggerUpdate, 10); // Trigger update after removal
                            }}
                          />
                        )
                      }
                    />
                  </Form.Item>
                  <Form.Item name={[field.name, "value"]}>
                    <Input hidden />
                  </Form.Item>
                </Flex>
              ))}
              {!isDisabled && !formValues?.isFrequency ? (
                <Form.Item>
                  <Button
                    className="success-button"
                    onClick={() => add({ label: "", value: uuidv4() })}
                    icon={<PlusOutlined />}
                  >
                    {t("Add")}
                  </Button>
                  {/* <Button
                    onClick={handleCleanDuplicates}
                    style={{ marginLeft: 8 }}
                    disabled={!hasDuplicateOption}
                  >
                    Clean Duplicates
                  </Button> */}
                </Form.Item>
              ) : null}
            </>
          );
        }}
      </Form.List>
        </>
      )}
      {
        !rest.parentTable ? (
          <Form.Item
            name="other"
            label={t("*Other* option")}
            className="horizontal-form-item"
          >
            <Switch size="small" />
          </Form.Item>)
          : null
      }
      {/* <Button size="small" className="mb-3" onClick={handleMasterUpdate}>Sync data</Button> */}


      <Form.Item name="sort" label={t("Sort")}>
        <Select options={sortOptions.map((option) => ({ label: t(option.label), value: option.value }))} allowClear />
      </Form.Item>
    </>
  );
};

export default DropdownForm;
